/**
 * The flavour guide's hero carousel.
 *
 * Six flavour families on a ring: one centred, one either side, one behind, the rest parked out of
 * sight. Stepping the ring moves every figure, swaps the watermark word and exchanges the copy block,
 * all on one 650ms curve, because they are one movement rather than three. The field itself does not
 * change: it is white on every slide, and the family's colour lives in the watermark, which each
 * slide carries inline.
 *
 * The element owns state and nothing else. Geometry, colour and easing live in the section's
 * stylesheet, keyed off `data-role` on each figure and `data-active` on each copy block, so a
 * designer can retune the ring without touching this file and so the band ships its first slide
 * already composed if the module never loads.
 *
 * Roles, for `n` slides and an active index `i`: the slide at `i` is `center`, `i+1` is `right`,
 * `i-1` is `left`, `i+2` is `back`, and everything else is `parked`. With six families that leaves
 * two parked, which is what keeps the ring from reading as a crowd.
 *
 * No auto-rotation, deliberately. WCAG 2.2.2 wants a pause control for anything that moves on its own,
 * and a hero that moves on its own also moves the destination out from under a reader who was aiming
 * at it. The WAI-ARIA carousel pattern's own advice is that auto-rotation starts paused; starting it
 * at never is the same answer with no control to explain.
 *
 * Accessibility contract, which the markup states and this file must not break:
 * - the deck is `role="region"` with `aria-roledescription="carousel"` and a name;
 * - each copy block is `role="group"` with `aria-roledescription="slide"` and a name that counts
 *   itself, "Icy & Cooling, slide 1 of 6";
 * - only the active block is visible, and `visibility: hidden` on the rest is what keeps their links
 *   out of the tab order without `inert` or a focus trap;
 * - the figures are one `aria-hidden` group: they are the same six families said a second time, in
 *   pictures, and a screen reader that hears the copy block does not need them.
 */

const DURATION = 650;
const ROLE_AT = { 0: 'center', 1: 'right', 2: 'back' };

class NastyWtfHero extends HTMLElement {
  connectedCallback() {
    this.figures = [...this.querySelectorAll('[data-hero-figure]')];
    this.copies = [...this.querySelectorAll('[data-hero-copy]')];
    this.ghosts = [...this.querySelectorAll('[data-hero-ghost]')];

    if (this.figures.length < 2) return;

    this.count = this.figures.length;
    this.index = 0;
    this.busy = false;

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);

    /*
     * The stylesheet ships slide one composed. Arming here rather than in the markup means a reader
     * with no JavaScript gets a still hero that is correct, not a stack of six figures waiting for a
     * script to sort them out.
     */
    this.dataset.armed = 'true';
    this.paint();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    clearTimeout(this.timer);
  }

  onClick(event) {
    const step = event.target.closest('[data-hero-step]');
    if (step) {
      this.go(step.dataset.heroStep === 'prev' ? -1 : 1);
      return;
    }

    /*
     * Tapping a device that is not the centred one brings it forward. It is the gesture everyone
     * tries on a carousel like this, and it costs no tab stops: the figures are `aria-hidden`
     * decoration and the arrows already do the same job for a keyboard.
     */
    const figure = event.target.closest('[data-hero-figure]');
    if (!figure || figure.dataset.role === 'center' || figure.dataset.role === 'parked') return;
    const target = this.figures.indexOf(figure);
    if (target >= 0) this.jump(target);
  }

  onKeydown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    /*
     * Only when the focus is on the band's own controls. Claiming the arrow keys for the whole hero
     * would take them off a reader trying to scroll the page from a focused link inside it.
     */
    if (!event.target.closest('[data-hero-step]')) return;
    event.preventDefault();
    this.go(event.key === 'ArrowLeft' ? -1 : 1);
  }

  go(delta) {
    this.jump((this.index + delta + this.count) % this.count);
  }

  /*
   * The lock is what stops a held-down arrow from queueing six half-finished transitions; it releases
   * on the same 650ms the stylesheet transitions on. Wrap-around rather than disabling a control at
   * either end, per the carousel pattern: a disabled arrow is a dead end a reader has to diagnose.
   */
  jump(target) {
    if (this.busy || target === this.index) return;

    this.index = target;
    this.paint();

    if (this.reduced) return;

    this.busy = true;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.busy = false;
    }, DURATION);
  }

  get reduced() {
    return matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  paint() {
    const { count, index } = this;

    this.figures.forEach((figure, i) => {
      const rel = (i - index + count) % count;
      figure.dataset.role = ROLE_AT[rel] || (rel === count - 1 ? 'left' : 'parked');
    });

    this.ghosts.forEach((ghost, i) => {
      ghost.dataset.active = String(i === index);
    });

    this.copies.forEach((copy, i) => {
      const active = i === index;
      copy.dataset.active = String(active);
      copy.setAttribute('aria-hidden', String(!active));
    });

    /*
     * The active family's colour is still published on the host even though the field is white and
     * each watermark carries its own tint inline. It costs one property write, and it is the hook
     * anything else on this band would read if it ever had to follow the slide.
     */
    const ground = this.figures[index].dataset.ground;
    if (ground) this.style.setProperty('--nz-hero-ground', ground);
  }
}

if (!customElements.get('nasty-wtf-hero')) {
  customElements.define('nasty-wtf-hero', NastyWtfHero);
}
